import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  useTheme,
  Avatar,
  Stack,
  CircularProgress,
  Alert,
  Grow,
} from '@mui/material';
import {
  LocationOn,
  LocalHospital,
  KeyboardArrowDown,
  Refresh,
  Star,
} from '@mui/icons-material';
import BookMyOpAPI from '../../services/BookMyOpapi';

const HospitalList = ({ dateFilter, sortOrder = '-star_rating' }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleHospitals, setVisibleHospitals] = useState(4);
  const [showingMore, setShowingMore] = useState(false);

  // Fetch top-rated hospitals from API
  const fetchHospitals = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build filters
      const filters = {
        page: 1,
        pageSize: 12,
        providerType: 'hospital', // Filter for hospitals only
        ordering: sortOrder, // Use sortOrder prop
      };
      
      // Add date filters if provided
      if (dateFilter) {
        if (dateFilter.availableDate) {
          filters.availableDate = dateFilter.availableDate;
        } else if (dateFilter.availableDateStart || dateFilter.availableDateEnd) {
          if (dateFilter.availableDateStart) {
            filters.availableDateStart = dateFilter.availableDateStart;
          }
          if (dateFilter.availableDateEnd) {
            filters.availableDateEnd = dateFilter.availableDateEnd;
          }
        }
      }
      
      // Use getServiceProviders API which supports date filters
      const response = await BookMyOpAPI.getServiceProviders(filters);
      
      if (response && response.results) {
        // Filter for top-rated hospitals (rating >= 4.0) and get first 12
        const topRatedHospitals = response.results
          .filter(hospital => hospital.star_rating && hospital.star_rating >= 4.0)
          .slice(0, 12);
        setHospitals(topRatedHospitals);
        setError(null);
      } else if (Array.isArray(response)) {
        const topRatedHospitals = response
          .filter(hospital => hospital.star_rating && hospital.star_rating >= 4.0)
          .slice(0, 12);
        setHospitals(topRatedHospitals);
        setError(null);
      } else {
        setError('No hospitals found');
        setHospitals([]);
      }
    } catch (err) {
      setError('Failed to fetch hospitals. Please try again later.');
      console.error('Error fetching hospitals:', err);
    } finally {
      setLoading(false);
    }
  }, [dateFilter, sortOrder]);

  // Fetch hospitals when component mounts or dateFilter changes
  useEffect(() => {
    fetchHospitals();
  }, [fetchHospitals]);

  const handleHospitalClick = (hospitalId) => {
    const params = new URLSearchParams();
    if (dateFilter) {
      if (dateFilter.availableDate) {
        params.set('availableDate', dateFilter.availableDate);
      } else {
        if (dateFilter.availableDateStart) {
          params.set('availableDateStart', dateFilter.availableDateStart);
        }
        if (dateFilter.availableDateEnd) {
          params.set('availableDateEnd', dateFilter.availableDateEnd);
        }
      }
    }
    const queryString = params.toString();
    navigate(`/hospitals/${hospitalId}${queryString ? `?${queryString}` : ''}`);
  };

  const handleViewDoctors = (hospitalId) => {
    // Navigate to find doctors with hospital ID as provider filter and date filter
    const params = new URLSearchParams();
    params.set('provider', hospitalId);
    if (dateFilter) {
      if (dateFilter.availableDate) {
        params.set('availableDate', dateFilter.availableDate);
      } else {
        if (dateFilter.availableDateStart) {
          params.set('availableDateStart', dateFilter.availableDateStart);
        }
        if (dateFilter.availableDateEnd) {
          params.set('availableDateEnd', dateFilter.availableDateEnd);
        }
      }
    }
    navigate(`/find-doctors?${params.toString()}`);
  };

  const handleShowMore = () => {
    setShowingMore(true);
    setTimeout(() => {
      setVisibleHospitals(prev => Math.min(prev + 4, hospitals.length));
      setShowingMore(false);
    }, 300);
  };

  const handleRefresh = () => {
    setVisibleHospitals(4);
  };

  const HospitalCard = ({ hospital, index }) => {
    const rating = hospital.star_rating; // Use API star_rating directly

    return (
      <Grow
        in={true}
        timeout={500 + (index % 4) * 200}
        style={{ transformOrigin: '0 0 0' }}
      >
        <Card
          sx={{
            // height: { xs: 260, md: 260, lg: 220 },
            borderRadius: 2.5,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            overflow: 'hidden',
            position: 'relative',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(0,0,0,0.06)',
            '&:hover': {
              transform: 'translateY(-8px) scale(1.02)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              zIndex: 10,
              '& .hospital-avatar': {
                transform: 'scale(1.1)',
              },
            },
          }}
          onClick={() => handleHospitalClick(hospital.id)}
        >
          <CardContent sx={{ p: { xs: 2, md: 2.5,lg: 2.5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Building Image */}
            {hospital.building_image && (
              <Box sx={{ mb: { xs: 1.5, md: 2 } }}>
                <Box
                  component="img"
                  src={hospital.building_image}
                  alt={`${hospital.name} building`}
                  sx={{
                    width: '100%',
                    height: { xs: 120, md: 140 },
                    objectFit: 'cover',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                />
              </Box>
            )}

            {/* Header - Hospital Name & Type */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: { xs: 1.5, md: 2 } }}>
              <Avatar
                className="hospital-avatar"
                sx={{
                  width: { xs: 45, md: 60 },
                  height: { xs: 45, md: 60 },
                  bgcolor: 'primary.main',
                  border: '2px solid #fff',
                  boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
                  transition: 'transform 0.3s ease',
                }}
              >
                <LocalHospital sx={{ fontSize: { xs: 20, md: 30 } }} />
              </Avatar>
              
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 900, 
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    lineHeight: 1.2,
                    mb: 0.3,
                    color: '#1a1a1a',
                  }}
                >
                  {hospital.name}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="primary" 
                  sx={{ 
                    fontWeight: 800, 
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    lineHeight: 1.1,
                  }}
                >
                  {hospital.provider_type_name}
                </Typography>
              </Box>
            </Box>

            {/* Location & Distance */}
            <Box sx={{ mb: { xs: 1, md: 1.5 } }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <LocationOn sx={{ fontSize: { xs: 14, md: 18 }, color: '#666' }} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    fontWeight: 800,
                    color: '#666',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {hospital.city && hospital.state 
                    ? `${hospital.city}, ${hospital.state}` 
                    : hospital.address_line1 || 'Location not available'}
                </Typography>
                {hospital.distance_km && (
                  <Chip
                    label={`${hospital.distance_km} km`}
                    size="small"
                    sx={{
                      height: { xs: 18, md: 20 },
                      fontSize: { xs: '0.6rem', md: '0.65rem' },
                      bgcolor: '#e3f2fd',
                      color: '#1976d2',
                      fontWeight: 700,
                      ml: 'auto !important',
                    }}
                  />
                )}
              </Stack>
              
              {/* Address Details */}
              {(hospital.address_line1 || hospital.locality) && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: { xs: '0.8rem', md: '0.85rem' },
                    fontWeight: 800,
                    color: '#888',
                    display: 'block',
                    mt: 0.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {hospital.address_line1 || hospital.locality}
                </Typography>
              )}
            </Box>

            {/* Rating - Only show if star_rating is not null */}
            {rating !== null && (
              <Box sx={{ mb: { xs: 1, md: 1.5 } }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Star sx={{ fontSize: { xs: 16, md: 18 }, color: '#ffa726' }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.9rem', md: '1rem' }, fontWeight: 800 }}>
                    {rating}
                  </Typography>
                </Stack>
              </Box>
            )}


            {/* Specialties */}
            {hospital.specialties && hospital.specialties.length > 0 && (
              <Box sx={{ mb: { xs: 1.5, md: 2 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' }, mb: 0.5, display: 'block', fontWeight: 800 }}>
                  Specialties:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {hospital.specialties.slice(0, 3).map((specialty, idx) => (
                    <Chip
                      key={idx}
                      label={specialty.name}
                      size="small"
                      sx={{
                        height: { xs: 18, md: 20 },
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        bgcolor: 'rgba(25, 118, 210, 0.1)',
                        color: 'primary.main',
                        fontWeight: 800,
                      }}
                    />
                  ))}
                  {hospital.specialties.length > 3 && (
                    <Chip
                      label={`+${hospital.specialties.length - 3}`}
                      size="small"
                      sx={{
                        height: { xs: 18, md: 20 },
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        bgcolor: 'rgba(0,0,0,0.1)',
                        color: 'text.secondary',
                        fontWeight: 800,
                      }}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* Footer - Single Action Button */}
            <Box sx={{ mt: 'auto' }}>
              <Button
                variant="contained"
                size="small"
                fullWidth
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: { xs: '0.9rem', md: '0.95rem' },
                  py: { xs: 0.8, md: 1 },
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewDoctors(hospital.id);
                }}
              >
                View Doctors
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grow>
    );
  };

  if (loading) {
    return (
      <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: '#ffffff' }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress size={50} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: '#ffffff' }}>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto' }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  if (hospitals.length === 0) {
    return (
      <Box sx={{ py: { xs: 3, md: 4 },mt:2, bgcolor: '#ffffff' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No hospitals available at the moment.
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: theme.palette.background.default }}>
      <Container maxWidth="xl">
        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mb: { xs: 2, md: 3 }, px: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              color: theme.palette.text.primary,
              textTransform:'uppercase',
              textAlign:'center',
            }}
          >
            Top Hospitals Near you
          </Typography>
          
          {visibleHospitals > 4 && (
            <IconButton
              onClick={handleRefresh}
              size="small"
              sx={{
                bgcolor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: 'grey.50' },
              }}
            >
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Stack>

        {/* Responsive Grid Layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: { xs: 2, md: 2.5 },
            px: { xs: 1, md: 2 },
          }}
        >
          {hospitals.slice(0, visibleHospitals).map((hospital, index) => (
            <HospitalCard key={hospital.id} hospital={hospital} index={index} />
          ))}
        </Box>

        {/* Show More / View All Actions */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          {visibleHospitals < hospitals.length ? (
            <Button
              variant="contained"
              onClick={handleShowMore}
              disabled={showingMore}
              startIcon={showingMore ? <CircularProgress size={16} /> : <KeyboardArrowDown />}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                // px: 4,
                // py: 1.5,
                fontSize: { xs: '0.7rem', md: '0.75rem' },
                mr: 2,
                background: 'linear-gradient(45deg, theme.palette.primary.main, theme.palette.secondary.main)',
                '&:hover': {
                  background: 'linear-gradient(45deg, theme.palette.primary.main, theme.palette.secondary.main)',
                },
              }}
            >
              {showingMore ? 'Loading...' : `Show More (${hospitals.length - visibleHospitals} left)`}
            </Button>
          ) : null}
          
          <Button
            variant="outlined"
            onClick={() => {
              const params = new URLSearchParams();
              if (dateFilter) {
                if (dateFilter.availableDate) {
                  params.set('availableDate', dateFilter.availableDate);
                } else {
                  if (dateFilter.availableDateStart) {
                    params.set('availableDateStart', dateFilter.availableDateStart);
                  }
                  if (dateFilter.availableDateEnd) {
                    params.set('availableDateEnd', dateFilter.availableDateEnd);
                  }
                }
              }
              const queryString = params.toString();
              navigate(`/hospitals${queryString ? `?${queryString}` : ''}`);
            }}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              // px: 4,
              // py: 1.5,
              fontSize: { xs: '0.7rem', md: '0.75rem' },
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.main,
                color: 'white',
              },
            }}
          >
            View All Hospitals
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default HospitalList; 